/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {AppSelection} from "@app/models/selection";
import {CommonState} from "@app/store/common/common.state";
import {PatientTreatmentPlanAdvState} from "@modules/doctor-panel/modules/patient-treatment-plan/store/patient-treatment-plan-adv.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {DATE_FMT_8601, TrecTranslation} from "@trec/api/common";
import {ReasonerWarningPrescription} from "@trec/api/reasoner";
import {C3DictionaryActions, C3DictionaryState} from "@trec/app-modules/dictionary-shared";
import {StoreAction} from "@trec/components/store";
import {format} from "date-fns";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {TpAdvActions} from "../../../store/patient-treatment-plan-adv.actions";

@Component({
  selector: "c3-config-warnings-patient",
  templateUrl: "./config-warnings-patient.component.html",
  styleUrls: ["./config-warnings-patient.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigWarningsPatientComponent
  extends ConfigSectionComponent
  implements OnInit, OnDestroy {
  @Select(PatientTreatmentPlanAdvState.warningsPatientPrescriptions)
  prescriptions$: Observable<ReasonerWarningPrescription[] | null>;
  @Select(
    hasActionsExecuting([
      C3DictionaryActions.FetchAvailableWarnings,
      TpAdvActions.FetchWarningsPatient,
      TpAdvActions.UpdateWarningsPatient,
    ])
  )
  loading$: Observable<boolean>;

  @Select(C3DictionaryState.availableWarnings)
  availableWarnings$: Observable<TrecTranslation[] | undefined>;

  @Select(CommonState.patientContext)
  context$: Observable<RegistryContexts | null | undefined>;

  @Input()
  context: RegistryContexts[];

  private valid = new BehaviorSubject(false);
  private touched = new BehaviorSubject(false);

  get touched$(): Observable<boolean> {
    return this.touched.asObservable();
  }

  get valid$(): Observable<boolean> {
    return this.valid.asObservable();
  }

  selection: AppSelection = {};

  private subscr: Subscription;

  constructor(private store: Store, private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // Fetch available list and current warnings prescriptions
    this.store.dispatch(new C3DictionaryActions.FetchAvailableWarnings({}));
    this.store.dispatch(new TpAdvActions.FetchWarningsPatient());

    // As default select all app features
    this.subscr = this.prescriptions$.subscribe((prescriptions) => {
      if (prescriptions) {
        // Set as selected all warnings that have a prescription
        this.selection = prescriptions.reduce((a, p) => ({...a, [p.warningId]: true}), {});

        this.valid.next(true);
        this.cd.markForCheck();
      }
    });
  }

  onSelectionChange() {
    this.touched.next(true);
  }

  prepareSaveDataAction(): StoreAction | null {
    const userEntry = this.store.selectSnapshot(CommonState.patientUserEntry);
    const currentPrescriptions =
      this.store.selectSnapshot(PatientTreatmentPlanAdvState.warningsPatientPrescriptions) || [];
    const availableWarnings = this.store.selectSnapshot(C3DictionaryState.availableWarnings);

    if (!(userEntry && userEntry[0].appPrescription)) {
      throw new Error(
        `Try to update warnings config but userEntry of current patient id is missing`
      );
    }
    if (!availableWarnings) {
      throw new Error(`Try to update warnings config but availableWarnings are unknown`);
    }

    const patientId = userEntry[0].patientId;

    // Insert only warnings that are selected but have no prescription  -> prescription needs to be created
    const insert: ReasonerWarningPrescription[] = Object.entries(this.selection)
      .filter((entry) => entry[1]) // Only selected
      .filter(
        (selectedW) => !currentPrescriptions.find((prescr) => prescr.warningId === selectedW[0])
      )
      .map((w) => ({
        // Create warning prescription
        trecUserId: patientId,
        warningId: w[0],
        deliveryType: "PUSH_PATIENT",
        targetId: patientId,
        from: format(new Date(), DATE_FMT_8601), // From now
        to: format(
          new Date((userEntry[0].appPrescription && userEntry[0].appPrescription.end) || 0),
          DATE_FMT_8601
        ), // Until app prescription end
        warningType: "patient",
      }));

    // Remove only warnings which are prescribed but not selected -> warnings to unprescribe
    const remove: ReasonerWarningPrescription[] = currentPrescriptions.filter(
      (prescr) => !this.selection[prescr.warningId]
    );

    // // Iterate on available warnings
    // const warningsToUpdate = availableWarnings
    //   // Filter warnings that are not selected and are not prescribed -> useless
    //   .filter(availableW => currentPrescriptions.find(w => w.warningId === availableW.id) || this.selection.isSelected(availableW.id));

    // // Select only warnings that are not already prescribed and has been selected -> prescription needs to be created
    // const insert: ReasonerWarningPrescription[] = warningsToUpdate
    //   .filter(availableW => !currentPrescriptions.find(w => w.warningId === availableW.id) && this.selection.isSelected(availableW.id))
    //   .map<ReasonerWarningPrescription>(availableW => ({ // Create warning prescription
    //     trecUserId: patientId,
    //     warningId: availableW.id,
    //     prego iveryType: 'PUSH_PATIENT',
    //     targetId: patientId,
    //     from: toISO8601Date(new Date()), // From now
    //     to: toISO8601Date(new Date(userEntry.appPrescription.end)), // Until app prescription end
    //     warningType: 'patient'
    //   }));

    // // Select only warnings that are prescribed but has been deselected
    // const stopTmp: ReasonerWarningPrescription[][] = warningsToUpdate
    //   .filter(availableW => currentPrescriptions.find(w => w.warningId === availableW.id) && !this.selection.isSelected(availableW.id))
    //   .map(availableW => currentPrescriptions.filter(w => w.warningId === availableW.id))
    //   .map((prescriptions: ReasonerWarningPrescription[]) => prescriptions.map(p => ({...p})));
    // // Flat map
    // const remove: ReasonerWarningPrescription[] = [].concat.apply([], stopTmp);

    return new TpAdvActions.UpdateWarningsPatient({insert, remove});
  }

  resetTouched(): void {
    this.touched.next(false);
  }

  ngOnDestroy() {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }

    this.valid.complete();
    this.touched.complete();
  }
}
